/**
 * Presentation helpers for inbound email bodies. An email is not a chat message: clients reply from
 * Gmail/Outlook and every reply drags the whole previous conversation quoted below the new text,
 * plus signatures and legal disclaimers. The thread already shows those messages above, so the quote
 * is noise that pushes the conversation off screen. SMS never has this shape and is left untouched.
 */

/** First line of a quoted block, in the formats the common clients produce. */
const QUOTE_HEADERS = [
    /^On .+ wrote:\s*$/i, // Gmail EN
    /^El .+ escribi(ó|o):\s*$/i, // Gmail ES
    /^-{2,}\s*Original Message\s*-{2,}/i,
    /^_{5,}\s*$/, // Outlook divider
    /^From:\s.+$/i, // Outlook EN header block
    /^De:\s.+$/i // Outlook ES header block
];

const isQuoteHeader = (line: string): boolean => QUOTE_HEADERS.some(pattern => pattern.test(line.trim()));

/** A run of `>` lines is a quote too, even without a header. */
const isQuotedLine = (line: string): boolean => line.trimStart().startsWith('>');

export interface EmailBodyParts {
    /** What the client actually wrote now. */
    visible: string;
    /** Everything from the quote marker down; undefined when there is nothing quoted. */
    quoted?: string;
}

/**
 * Splits the body into the new text and the quoted history. Conservative on purpose: when the split
 * would leave nothing visible (a reply written *below* the quote, or a forward), the whole body is
 * kept as visible so no content is ever hidden by accident.
 */
export const splitQuotedReply = (body: string): EmailBodyParts => {
    const lines = body.split(/\r?\n/);
    const index = lines.findIndex(line => isQuoteHeader(line) || isQuotedLine(line));

    if (index <= 0) return { visible: body };

    const visible = lines.slice(0, index).join('\n').trimEnd();
    if (visible.trim().length === 0) return { visible: body };

    return { visible, quoted: lines.slice(index).join('\n').trim() };
};

/** Gmail leaves this marker in the plain-text part where an inline image was. */
const INLINE_IMAGE_PLACEHOLDER = /^\s*\[image:\s*.+?\]\s*$/gim;

export const stripInlineImagePlaceholders = (body: string): string =>
    body
        .replace(INLINE_IMAGE_PLACEHOLDER, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
